<template>
  <bb-modal :visible="visible" @close="$emit('close')" flavor="card" full-mobile>
    <template #heading>
      <p class="f-color-blue"> {{ translations.title }} </p>
    </template>
    <bb-form @progress="onProgress" class="pending-modal">
      <bb-select
        framed
        :label="translations.account"
        name="account"
        v-model="form.accountId"
        :options="accountOptions"
        v-validate="'required|unique-beneficiary-iban'"
        :data-vv-as="translations.account"
        :placeholder="translations.selectAccount"
        data-testid="pending-payment-account-select"
      />
      <bb-input
        framed
        :label="translations.recipientName"
        name="recipientName"
        v-validate="'required|spaceCannotBeFirstChar|beneficiary-name|beneficiary-name-length'"
        v-model="form.recipientName"
        :loading="beneficiaryValidation.isLoading"
        :data-vv-as="translations.recipientName"
        data-testid="pending-payment-recipient-name"
        data-vv-validate-on="change|input"
      />
      <bb-input
        framed
        :label="translations.recipientIban"
        :formatter="formatIban"
        name="recipientIban"
        v-validate="'required|iban|beneficiary-iban|unique-beneficiary-iban'"
        v-model.trim="form.recipientIban"
        :loading="beneficiaryValidation.isLoading"
        :data-vv-as="translations.recipientIban"
        data-testid="pending-payment-recipient-iban"
        data-vv-validate-on="change|input"
      />
      <bb-input
        framed
        :label="translations.amount"
        v-validate="'required|min_value:0.01'"
        :data-vv-as="translations.amount"
        v-model.number="form.amount"
        name="amount"
        :currency="true"
        :locale="locale"
        :add-on="currencySymbol"
        data-vv-validate-on="change|input"
        data-testid="pending-payment-amount"
        :inputAttrs="{autocomplete: 'off'}"
      />
      <bb-input
        framed
        :label="translations.description"
        :data-vv-as="translations.description"
        name="description"
        ref="description"
        v-model="form.description"
        v-validate="'spaceCannotBeFirstChar|descriptionCanContainOnlyFollowingsCharacters|descriptionLength'"
        v-validate.continues="'atLeastOneFilled:referenceNumber'"
        data-testid="pending-payment-description"
      />
      <bb-input
        framed
        type="text"
        :label="translations.referenceNumber"
        :data-vv-as="translations.referenceNumber"
        :maxlength="maxInputLengths.reference"
        name="referenceNumber"
        ref="referenceNumber"
        v-model.trim="form.referenceNumber"
        v-validate.continues="'atLeastOneFilled:description|referenceCorrectness'"
        data-testid="pending-payment-reference-number"
      />
      <div slot="submit" slot-scope="{}"/>
    </bb-form>
    <template #footer>
      <bb-button
        data-testid="pending-payment-save-button"
        :label="translations.save"
        v-bind="buttonProps"
        display="block"
        corner="sharpMobileOnly"
        class="m-s-a"
        @click="onSubmit"
      />
    </template>
  </bb-modal>
</template>

<script>
import paymentsMixin from '@account/views/payments/payments.mixin'
import { mapActions, mapState } from 'pinia'
import { usePendingPaymentStore } from '@account/store/pendingPaymentStore'
import dayjs from 'dayjs'

export default {
  name: 'modify-pending-modal',
  mixins: [paymentsMixin],
  props: {
    visible: {
      type: Boolean,
      required: true,
      default: false
    },
    modifyPaymentId: {
      type: Number,
      required: true
    }
  },
  methods: {
    ...mapActions(usePendingPaymentStore, ['modifyPendingPayment']),
    async onSubmit () {
      try {
        this.loaders.submit = true
        const {
          accountId,
          createdTime,
          ...neededKeys
        } = this.form
        const body = {
          fromIban: this.selectedAccount.iban,
          currencyCode: this.selectedAccount.currencyCode,
          // Payments in the past are not allowed
          orderDate: dayjs().format('YYYY-MM-DD'),
          ...neededKeys
        }
        await this.modifyPendingPayment(this.modifyPaymentId, body)
        this.$notify({
          type: 'success',
          duration: 5000,
          text: this.translations.modifyNotification.text,
          title: this.translations.modifyNotification.title
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          key: 'generalError',
          duration: 10000,
          correlationId: error.correlationId
        })
      } finally {
        this.loaders.submit = false
        this.$emit('close')
      }
    }
  },
  computed: {
    ...mapState(usePendingPaymentStore, ['pendingPayments']),
    translations () {
      return {
        title: this.$pgettext('pending_payment_modify', 'Edit pending payment'),
        heading: this.$pgettext('payment_form', 'Outgoing payment'),
        account: this.$pgettext('payment_form', 'From account'),
        recipientName: this.$pgettext('payment_form', 'Recipient name'),
        recipientIban: this.$pgettext('payment_form', 'Recipient IBAN'),
        amount: this.$pgettext('payment_form', 'Amount'),
        description: this.$pgettext('payment_form', 'Description'),
        referenceNumber: this.$pgettext('payment_form', 'Reference number'),
        selectAccount: this.$pgettext('payment_form', 'Select account you wish to transfer from'),
        save: this.$pgettext('pending_payment_modify', 'Confirm changes'),
        modifyNotification: {
          title: this.$pgettext('pending_payment_modify', 'Pending payment modified'),
          text: this.$pgettext('pending_payment_modify', 'You\'re all set to use this pending payment')
        }
      }
    },
    selectedPendingPayment () {
      return this.pendingPayments.find(payment => payment.id === this.modifyPaymentId)
    },
    buttonProps () {
      return {
        ...this.button,
        disabled: this.button.disabled
      }
    }
  },
  mounted () {
    this.getAccounts()
    this.setPrefilledData(this.selectedPendingPayment)
  }
}
</script>

<style scoped lang="scss">
.pending-modal {
  max-width: 425px;

  @media (max-width: $mobile-view-breaking-point) {
    max-width: none;
  }

  .description {
    line-height: 18px;
    text-align: center;
  }
}
</style>
